import { useEffect, useState } from 'react';
import type { TextPart } from '../types.js';
import { renderMarkdown } from './markdown.js';

export interface TextBlockProps {
  readonly parts: readonly TextPart[];
  /** Type the text out character by character (a freshly produced answer). */
  readonly typing?: boolean;
  /** Called once when the last character is shown (or at once when not typing). */
  readonly onTyped?: () => void;
  /** Called whenever more text became visible (the stream follows it). */
  readonly onProgress?: (() => void) | undefined;
}

const TICK_MS = 18;
/** A long answer is typed in at most this long, whatever its length. */
const MAX_TYPING_MS = 6000;

/** The prose as Markdown source: headline figures (`strong` parts) become `**bold**`. */
function toMarkdown(parts: readonly TextPart[]): string {
  return parts.map((part) => (typeof part === 'string' ? part : `**${part.strong}**`)).join('');
}

/** Characters shown so far; completes immediately for reduced-motion users. */
function useTypedCount(total: number, enabled: boolean, onDone: (() => void) | undefined): number {
  const [count, setCount] = useState(enabled ? 0 : total);
  useEffect(() => {
    if (!enabled || total === 0) {
      setCount(total);
      onDone?.();
      return;
    }
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setCount(total);
      onDone?.();
      return;
    }
    const step = Math.max(1, Math.ceil(total / (MAX_TYPING_MS / TICK_MS)));
    let shown = 0;
    const timer = window.setInterval(() => {
      shown = Math.min(total, shown + step);
      setCount(shown);
      if (shown >= total) {
        window.clearInterval(timer);
        onDone?.();
      }
    }, TICK_MS);
    return () => window.clearInterval(timer);
    // The text of a produced answer does not change; run once per mount.
  }, []);
  return count;
}

/** Component 1 of 8 — the answer's prose, rendered as Markdown. */
export function TextBlock({ parts, typing = false, onTyped, onProgress }: TextBlockProps) {
  const source = toMarkdown(parts);
  const chars = Array.from(source);
  const count = useTypedCount(chars.length, typing, onTyped);
  const done = count >= chars.length;
  const shown = done ? source : chars.slice(0, count).join('');
  useEffect(() => {
    onProgress?.();
  }, [count, onProgress]);
  return (
    <div className="ai-block ai-text">
      {renderMarkdown(shown)}
      {typing && !done ? <span className="ai-caret" aria-hidden="true" /> : null}
    </div>
  );
}

/** Plain-text rendering of the prose (clipboard). */
export function textPartsToString(parts: readonly TextPart[]): string {
  return parts.map((part) => (typeof part === 'string' ? part : part.strong)).join('');
}
